/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag
 */
import React, { useCallback, useState } from 'react';

import { css as cssUnbounded } from '@compiled/react';
import { useIntl } from 'react-intl';

import { cssMap, jsx } from '@atlaskit/css';
import { Box, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { ResearchExpandable } from '../research-expandable';

import messages from './messages';
import { ResearchStepItemListRefresh } from './ResearchStepItemListRefresh';
import { SingleResearchCompactPreview } from './SingleResearchCompactPreview';

export type CompactResearchStep = {
	separatedContent: string[];
	stepId: string;
	title: React.ReactElement | string;
	completed?: boolean;
	isStreamed?: boolean;
	/** Optional SVG icon URL for the tool that generated this step */
	toolIcon?: string;
	/** Optional name of the tool that generated this step (e.g. "TeamworkAgent", "execute_bash") */
	toolName?: string;
	/** Optional list of product icon URLs to cycle through while the step is in progress */
	sourceIcons?: string[];
	/** Documents found during this research step */
	documents?: Array<{ title: string; url: string; product?: string }>;
};

export const TRACE_INTERACTION_TYPES = {
	ALL_TRACES: 'allTracesExpandable',
	SINGLE_TRACE: 'singleTraceExpandable',
} as const;

export type TraceInteractionEvent = {
	type: (typeof TRACE_INTERACTION_TYPES)[keyof typeof TRACE_INTERACTION_TYPES];
	expanded: boolean;
};

export type CompactResearchStepsProps = {
	steps?: CompactResearchStep[];
	researchCompleted?: boolean;
	responseStreaming?: boolean;
	renderSubstep: (substep: string, isStreamed?: boolean) => React.ReactNode;
	thinkDeeperTriggered?: boolean;
	/** Optional slot to render alongside the thinking message header (e.g. Answer Now button) */
	answerNowSlot?: React.ReactNode;
	/** Called when the user clicks to expand/collapse traces — either all traces or a single step. */
	onTraceInteraction?: (event: TraceInteractionEvent) => void;
	/** Optional function to modify/wrap each source URL before it is rendered. */
	xpcWrapUrl?: (url: string, stripOnly: boolean) => string;
};

const timelineConnectorLineStyle = cssUnbounded({
	position: 'absolute',
	left: '11px',
	top: token('space.300'),
	bottom: token('space.300'),
	width: '2px',
	backgroundColor: token('color.border'),
	zIndex: 0,
});

const styles = cssMap({
	researchStepList: {
		listStyleType: 'none',
		paddingInline: token('space.0'),
		position: 'relative',
	},
});

export const CompactResearchSteps = ({
	steps = [],
	researchCompleted = false,
	responseStreaming = false,
	renderSubstep,
	thinkDeeperTriggered = false,
	answerNowSlot,
	onTraceInteraction,
	xpcWrapUrl,
}: CompactResearchStepsProps): JSX.Element => {
	const { formatMessage } = useIntl();
	const lastStep = steps.at(-1);

	// Expand request tokens keyed by step id. Each token is a
	// MutableRefObject<boolean> — `.current === true` means "expand needed".
	// When a child item consumes the token it sets `.current = false` (no
	// re-render). The map survives the preview → completed branch switch so
	// the completed-phase items can honour unmet requests on mount.
	// Consumed (spent) tokens remain in the map so the outer steps-summary
	// expandable stays open; the map is only cleared when the user explicitly
	// collapses the outer expandable.
	const [expandRequests, setExpandRequests] = useState<
		Record<string, React.MutableRefObject<boolean>>
	>({});

	const shouldShowStepsSummary = responseStreaming || (researchCompleted && steps.length > 0);

	// Called by preview children when the user clicks expand/collapse.
	const handleUserExpandToggle = useCallback((stepId: string, expanded: boolean) => {
		if (expanded) {
			setExpandRequests((prev) => ({ ...prev, [stepId]: { current: true } }));
		} else {
			setExpandRequests((prev) => {
				const { [stepId]: _, ...rest } = prev;
				return rest;
			});
		}
	}, []);

	// Called when the preview's main expandable is opened or collapsed.
	// On expand: add a sentinel token so the outer steps-summary auto-opens
	// post-streaming (the sentinel won't match any real step.stepId).
	// On collapse: clear ALL accumulated requests — the user explicitly
	// closed the preview, so nothing should carry over to the completed phase.
	const handlePreviewExpandChanged = useCallback((expanded: boolean) => {
		if (expanded) {
			setExpandRequests((prev) => ({ ...prev, __parent__: { current: true } }));
		} else {
			setExpandRequests({});
		}
	}, []);

	// When there are expand requests (met or unmet), the outer "steps summary"
	// expandable should auto-open so the persisted expansion is visible without
	// an extra user click.
	const stepsSummaryDefaultExpanded = Object.keys(expandRequests).length > 0;

	return (
		<Box>
			{shouldShowStepsSummary ? (
				<ResearchExpandable
					defaultExpanded={stepsSummaryDefaultExpanded}
					showExpandIcon={true}
					showExpandAtEnd={false}
					onExpandClick={(expanded) => {
						onTraceInteraction?.({ type: TRACE_INTERACTION_TYPES.ALL_TRACES, expanded });
						// When the user explicitly collapses the steps-summary expandable,
						// clear all expand requests so it won't auto-reopen.
						if (!expanded) {
							setExpandRequests({});
						}
					}}
					title={
						<Text size="medium" weight="regular" color="color.text.subtlest">
							{steps.length === 1
								? formatMessage(messages.thinkingCompleted)
								: formatMessage(messages.stepsSummary, { total: steps.length })}
						</Text>
					}
				>
					<Box as="ul" xcss={styles.researchStepList} testId="research-step-list">
						{steps.length > 1 && <div css={timelineConnectorLineStyle} />}
						{steps.map((step, index) => {
							const isLastStep = index === steps.length - 1;
							const isInProgress = step.completed === false || !researchCompleted;
							// Always show the step icon — never hide it even when there's only one step
							// and research is completed.
							const shouldHideIcon = false;
							return (
								<ResearchStepItemListRefresh
									key={step.stepId}
									step={step}
									isLastItem={isLastStep}
									isInProgress={isInProgress}
									hideIcon={shouldHideIcon}
									renderSubstep={renderSubstep}
									disableShimmer={true}
									forceSubstepCollapsed={true}
									previewSubstep={
										step.toolName === 'execute_bash' ? undefined : step.separatedContent[0]
									}
									titleSize="small"
									titleWeight="medium"
									expandRequest={expandRequests[step.stepId] ?? null}
									onTraceInteraction={onTraceInteraction}
									xpcWrapUrl={xpcWrapUrl}
								/>
							);
						})}
					</Box>
				</ResearchExpandable>
			) : lastStep ? (
				<SingleResearchCompactPreview
					title={lastStep.title}
					separatedContent={lastStep.separatedContent}
					steps={steps}
					renderSubstep={renderSubstep}
					researchCompleted={researchCompleted}
					thinkDeeperTriggered={thinkDeeperTriggered}
					isLastStepStreamed={lastStep.isStreamed}
					answerNowSlot={answerNowSlot}
					onTraceInteraction={onTraceInteraction}
					onUserExpandToggle={handleUserExpandToggle}
					onPreviewExpandChanged={handlePreviewExpandChanged}
				/>
			) : null}
		</Box>
	);
};
